import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDiscordSettings, saveDiscordSettings } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function DiscordPage() {
  const { orgId } = useParams();
  const { user, organizations } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    guildId: '',
    channelId: '',
    enabled: false,
    notifyNewEvent: true,
    notifyReminder: true,
    notifyEventUpdate: true,
    reminderMinutes: 60,
    embedColor: '#5865F2',
    eventEmbedColor: '#5865F2',
    reminderEmbedColor: '#F59E0B'
  });

  const org = organizations.find(o => o._id === orgId);
  const isOwner = org?.ownerId === user?._id || user?.role === 'admin';

  useEffect(() => { loadSettings(); }, [orgId]);

  const loadSettings = async () => {
    try {
      const res = await getDiscordSettings(orgId);
      setSettings(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const res = await saveDiscordSettings(orgId, settings);
      setSettings(res.data);
      setSuccess('Discord settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Discord Integration</h2>
          <div className="subtitle">{org?.name || 'Organization'}</div>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {!isOwner ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <ExclamationTriangleIcon style={{ width: 48, height: 48, color: 'var(--text-muted)', marginBottom: 12 }} />
          <h3 style={{ color: 'var(--text-secondary)' }}>Access Restricted</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Only admins can manage Discord settings.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 20, maxWidth: 720 }}>
          {/* Setup Guide */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#5865F2' }}>
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
              </svg>
              How to connect your Discord bot
            </h3>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, padding: '10px 14px', background: 'rgba(88,101,242,0.1)', border: '1px solid rgba(88,101,242,0.3)', borderRadius: 8 }}>
              <strong>⚠️ Before you start:</strong> To use the ClubMaster Discord bot, you must first open a ticket on our Discord server requesting the bot to be added to your server, or contact <strong>@kumi_ss</strong> directly.
            </div>
            <ol style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
              <li>Enable <strong>Developer Mode</strong> in Discord: Settings → Advanced → Developer Mode</li>
              <li>Right-click your <strong>server name</strong> → Copy Server ID → paste below</li>
              <li>Right-click the <strong>channel</strong> where you want notifications → Copy Channel ID → paste below</li>
              <li>Save settings and enable notifications</li>
              <li>Make sure your Discord bot is running and configured with the ClubMaster API URL</li>
            </ol>
          </div>

          {/* Settings Form */}
          <form onSubmit={handleSave}>
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Discord IDs</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Server ID (Guild ID)</label>
                  <input
                    className="form-input"
                    value={settings.guildId}
                    onChange={e => setSettings(s => ({ ...s, guildId: e.target.value.replace(/\D/g, '') }))}
                    placeholder="e.g. 123456789012345678"
                    maxLength={20}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Right-click server → Copy Server ID
                  </div>
                </div>
                <div className="form-group">
                  <label>Channel ID</label>
                  <input
                    className="form-input"
                    value={settings.channelId}
                    onChange={e => setSettings(s => ({ ...s, channelId: e.target.value.replace(/\D/g, '') }))}
                    placeholder="e.g. 123456789012345678"
                    maxLength={20}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Right-click channel → Copy Channel ID
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Notification Types</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.notifyNewEvent}
                      onChange={e => setSettings(s => ({ ...s, notifyNewEvent: e.target.checked }))}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>New Event Created</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Bot sends an embed when a new event is added to the calendar</div>
                    </div>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.notifyReminder}
                      onChange={e => setSettings(s => ({ ...s, notifyReminder: e.target.checked }))}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>Event Reminder</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Bot sends a reminder with @here before an event starts</div>
                    </div>
                  </label>

                  {settings.notifyReminder && (
                    <div className="form-group" style={{ marginLeft: 24, marginTop: 4 }}>
                      <label style={{ fontSize: 13 }}>Reminder time before event</label>
                      <select
                        className="form-input"
                        style={{ width: 'auto', minWidth: 160 }}
                        value={settings.reminderMinutes || 60}
                        onChange={e => setSettings(s => ({ ...s, reminderMinutes: Number(e.target.value) }))}
                      >
                        <option value={5}>5 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={120}>2 hours</option>
                      </select>
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.notifyEventUpdate !== false}
                      onChange={e => setSettings(s => ({ ...s, notifyEventUpdate: e.target.checked }))}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>Event Updated</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Bot sends a notification when an event is edited (time, location, etc.)</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label style={{ fontSize: 14, fontWeight: 600 }}>Embed Colors</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>New event announcement</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 36, flexShrink: 0, cursor: 'pointer', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                        <div style={{ width: '100%', height: '100%', background: settings.eventEmbedColor || settings.embedColor || '#5865F2' }} />
                        <input type="color" value={settings.eventEmbedColor || settings.embedColor || '#5865F2'} onChange={e => setSettings(s => ({ ...s, eventEmbedColor: e.target.value }))} style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
                      </label>
                      <input
                        className="form-input"
                        value={settings.eventEmbedColor || settings.embedColor || '#5865F2'}
                        onChange={e => { const v = e.target.value; if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setSettings(s => ({ ...s, eventEmbedColor: v })); }}
                        placeholder="#5865F2"
                        maxLength={7}
                        style={{ width: 110, fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Reminder</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 36, flexShrink: 0, cursor: 'pointer', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                        <div style={{ width: '100%', height: '100%', background: settings.reminderEmbedColor || '#F59E0B' }} />
                        <input type="color" value={settings.reminderEmbedColor || '#F59E0B'} onChange={e => setSettings(s => ({ ...s, reminderEmbedColor: e.target.value }))} style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
                      </label>
                      <input
                        className="form-input"
                        value={settings.reminderEmbedColor || '#F59E0B'}
                        onChange={e => { const v = e.target.value; if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setSettings(s => ({ ...s, reminderEmbedColor: v })); }}
                        placeholder="#F59E0B"
                        maxLength={7}
                        style={{ width: 110, fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Event update</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 36, flexShrink: 0, cursor: 'pointer', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                        <div style={{ width: '100%', height: '100%', background: settings.updateEmbedColor || '#FFA500' }} />
                        <input type="color" value={settings.updateEmbedColor || '#FFA500'} onChange={e => setSettings(s => ({ ...s, updateEmbedColor: e.target.value }))} style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
                      </label>
                      <input
                        className="form-input"
                        value={settings.updateEmbedColor || '#FFA500'}
                        onChange={e => { const v = e.target.value; if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setSettings(s => ({ ...s, updateEmbedColor: v })); }}
                        placeholder="#FFA500"
                        maxLength={7}
                        style={{ width: 110, fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 20, padding: '16px 0', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))}
                  />
                  Enable Discord Notifications
                </label>
                {settings.enabled && (
                  <span className="badge badge-success" style={{ fontSize: 11 }}>
                    <CheckCircleIcon className="icon" /> Active
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>

          {/* Status Card */}
          {(settings.guildId || settings.channelId) && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Connection Status</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                <div>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Status</div>
                  <div style={{ fontWeight: 600, color: settings.enabled ? 'var(--success)' : 'var(--text-muted)' }}>
                    {settings.enabled ? '● Active' : '○ Disabled'}
                  </div>
                </div>
                {settings.guildId && (
                  <div>
                    <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Server ID</div>
                    <div style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 12 }}>{settings.guildId}</div>
                  </div>
                )}
                {settings.channelId && (
                  <div>
                    <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Channel ID</div>
                    <div style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 12 }}>{settings.channelId}</div>
                  </div>
                )}
                <div>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>New Events</div>
                  <div style={{ fontWeight: 500, color: settings.notifyNewEvent ? 'var(--success)' : 'var(--text-muted)' }}>
                    {settings.notifyNewEvent ? '✓ On' : '✗ Off'}
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Reminders</div>
                  <div style={{ fontWeight: 500, color: settings.notifyReminder ? 'var(--success)' : 'var(--text-muted)' }}>
                    {settings.notifyReminder ? '✓ On' : '✗ Off'}
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Event Updates</div>
                  <div style={{ fontWeight: 500, color: settings.notifyEventUpdate !== false ? 'var(--success)' : 'var(--text-muted)' }}>
                    {settings.notifyEventUpdate !== false ? '✓ On' : '✗ Off'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
